import React, { Component } from "react";
import { Link } from "react-router-dom";

import "../../styles/newDesign.scss";
import "../../components/HomeBlocks/HomeBlocks.scss";
import "./Library.scss";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

import { resolveTenant, isLoggedIn, searchLibrary, libraryFavorites, libraryHistory, setFavorite } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import { durationLabel, TAG_TYPES, DURATION_PRESETS, LEVELS, nextTags } from "../../Helper/library";
import Toast from "../../components/Toast/Toast";

const properties = require("../../properties.json");

const EMPTY = { q: "", level: "", instructorId: "", duration: -1, tagIds: [], sort: "new", page: 0 };

/** The member's library of videos (mode "library") or recipes (mode "recipes"): search, filters, favorites and what
 *  they watched last. A card opens the item inside its course, where the usual access rules apply. */
class Library extends Component {
  state = { tenant: null, notFound: false, tab: "all", filters: { ...EMPTY }, result: null, list: null, loading: true, error: null };

  componentDidMount() {
    if (!isLoggedIn()) {
      window.location.href = "/sign-in";
      return;
    }
    resolveTenant()
      .then((tenant) => {
        document.title = properties.site_name + " - " + t(this.title());
        this.setState({ tenant });
        this.load();
      })
      .catch(() => this.setState({ notFound: true }));
  }

  componentDidUpdate(prev) {
    if (prev.mode !== this.props.mode) {
      this.setState({ tab: "all", filters: { ...EMPTY }, result: null, list: null }, this.load);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  recipes = () => this.props.mode === "recipes";
  title = () => (this.recipes() ? "lib.recipes" : "lib.library");

  params = () => {
    const f = this.state.filters;
    const preset = DURATION_PRESETS[f.duration] || {};
    return {
      q: f.q.trim() || undefined,
      level: f.level || undefined,
      instructorId: f.instructorId || undefined,
      minMinutes: preset.min,
      maxMinutes: preset.max,
      tagIds: f.tagIds.length ? f.tagIds.join(",") : undefined,
      type: this.recipes() ? "ARTICLE" : "VIDEO",
      sort: f.sort,
      page: f.page,
      size: 12,
    };
  };

  load = () => {
    const { tab } = this.state;
    this.setState({ loading: true, error: null });
    const done = (patch) => this.setState({ loading: false, ...patch });
    if (tab === "favorites" || tab === "history") {
      (tab === "favorites" ? libraryFavorites() : libraryHistory())
        .then((list) => done({ list: (list || []).filter((c) => c.type === (this.recipes() ? "ARTICLE" : "VIDEO")) }))
        .catch((e) => done({ list: [], error: e.message }));
      return;
    }
    searchLibrary(this.params())
      .then((result) => done({ result }))
      .catch((e) => done({ error: e.message }));
  };

  setFilters = (patch, debounce) => {
    this.setState({ filters: { ...this.state.filters, page: 0, ...patch } }, () => {
      clearTimeout(this.timer);
      if (debounce) this.timer = setTimeout(this.load, 350);
      else this.load();
    });
  };

  setPage = (page) => this.setState({ filters: { ...this.state.filters, page } }, this.load);
  setTab = (tab) => this.setState({ tab }, this.load);

  toggleFavorite = (card) => {
    const on = !card.favorite;
    setFavorite(card.itemId, on)
      .then(() => {
        const flip = (c) => (c.itemId === card.itemId ? { ...c, favorite: on } : c);
        this.setState((s) => ({
          result: s.result ? { ...s.result, items: s.result.items.map(flip) } : s.result,
          list: s.list ? (s.tab === "favorites" && !on ? s.list.filter((c) => c.itemId !== card.itemId) : s.list.map(flip)) : s.list,
        }));
      })
      .catch((e) => this.setState({ error: e.message }));
  };

  renderFilters() {
    const { result, filters } = this.state;
    const facets = result && result.facets;
    return (
      <aside className="lib-filters">
        <input className="lib-search" type="search" placeholder={t("lib.search")} value={filters.q}
          onChange={(e) => this.setFilters({ q: e.target.value }, true)} />

        {!this.recipes() && (
          <>
            <h4>{t("lib.duration")}</h4>
            <div className="lib-chips">
              {DURATION_PRESETS.map((p, i) => (
                <button type="button" key={i} className={`lib-chip ${filters.duration === i ? "on" : ""}`}
                  onClick={() => this.setFilters({ duration: filters.duration === i ? -1 : i })}>{t(p.label)}</button>
              ))}
            </div>
          </>
        )}

        {facets && facets.levels.length > 0 && !this.recipes() && (
          <>
            <h4>{t("lib.level")}</h4>
            <div className="lib-chips">
              {facets.levels.map((l) => (
                <button type="button" key={l.key} className={`lib-chip ${filters.level === l.key ? "on" : ""}`}
                  onClick={() => this.setFilters({ level: filters.level === l.key ? "" : l.key })}>
                  {t(LEVELS[l.key] || l.label)} <small>{l.count}</small>
                </button>
              ))}
            </div>
          </>
        )}

        {facets && facets.instructors.length > 1 && (
          <>
            <h4>{t("lib.trainer")}</h4>
            <select value={filters.instructorId} onChange={(e) => this.setFilters({ instructorId: e.target.value })}>
              <option value="">{t("lib.allTrainers")}</option>
              {facets.instructors.map((i) => <option key={i.key} value={i.key}>{i.label} ({i.count})</option>)}
            </select>
          </>
        )}

        {facets && TAG_TYPES.map((type) => {
          const list = facets.tags.filter((x) => x.type === type);
          if (!list.length) return null;
          return (
            <React.Fragment key={type}>
              <h4>{t("lib.tag." + type)}</h4>
              <div className="lib-chips">
                {list.map((x) => (
                  <button type="button" key={x.id} className={`lib-chip ${filters.tagIds.indexOf(x.id) >= 0 ? "on" : ""}`}
                    onClick={() => this.setFilters({ tagIds: nextTags(filters.tagIds, x.id) })}>
                    {x.name} <small>{x.count}</small>
                  </button>
                ))}
              </div>
            </React.Fragment>
          );
        })}

        <h4>{t("lib.sort")}</h4>
        <select value={filters.sort} onChange={(e) => this.setFilters({ sort: e.target.value })}>
          <option value="new">{t("lib.sortNew")}</option>
          <option value="duration">{t("lib.sortDuration")}</option>
          <option value="title">{t("lib.sortTitle")}</option>
        </select>
        <button type="button" className="lib-clear" onClick={() => this.setFilters({ ...EMPTY })}>{t("lib.clear")}</button>
      </aside>
    );
  }

  renderCard = (c) => (
    <article className={`lib-card ${c.locked ? "locked" : ""}`} key={c.itemId}>
      <Link to={`/courses/${c.courseId}?item=${c.itemId}`} className="lib-thumb" style={c.thumb ? { backgroundImage: `url(${c.thumb})` } : undefined}>
        {c.locked && <i className="fas fa-lock" aria-hidden="true" />}
        {c.durationSeconds ? <span className="lib-dur">{durationLabel(c.durationSeconds)}</span> : null}
      </Link>
      <button type="button" className={`lib-fav ${c.favorite ? "on" : ""}`} aria-label={t("lib.favorite")} aria-pressed={c.favorite}
        onClick={() => this.toggleFavorite(c)}>
        <i className={c.favorite ? "fas fa-heart" : "far fa-heart"} aria-hidden="true" />
      </button>
      <div className="lib-body">
        <h3><Link to={`/courses/${c.courseId}?item=${c.itemId}`}>{c.title}</Link></h3>
        <div className="lib-meta">
          {c.level && <span className="hb-chip">{t(LEVELS[c.level] || c.level)}</span>}
          {c.instructorName && <span>{c.instructorName}</span>}
          {c.watchedSeconds ? <span>{t("lib.stoppedAt", { time: durationLabel(c.watchedSeconds) })}</span> : null}
        </div>
        {c.tags && c.tags.length > 0 && <div className="lib-tags">{c.tags.map((x) => <span key={x.id}>#{x.name}</span>)}</div>}
      </div>
    </article>
  );

  render() {
    const { tenant, notFound, tab, result, list, loading, error } = this.state;
    if (notFound) return <div className="page"><div className="container text-center"><p>{t("notFound")}</p></div></div>;
    if (!tenant) return null;

    const cards = tab === "all" ? (result ? result.items : []) : list || [];
    return (
      <>
        <Header tenant={tenant} />
        <div className="nd-page hb-page library">
          <div className="hb-wrap">
            <h1 className="hb-title nd-title">{t(this.title())}</h1>
            <div className="lib-tabs" role="tablist">
              {[["all", this.recipes() ? "lib.allRecipes" : "lib.all"], ["favorites", "lib.favorites"], ["history", "lib.history"]].map(([key, label]) => (
                <button type="button" role="tab" key={key} aria-selected={tab === key} className={tab === key ? "on" : ""} onClick={() => this.setTab(key)}>{t(label)}</button>
              ))}
            </div>
            <Toast msg={error} type="error" />
            <div className={`lib-layout ${tab === "all" ? "" : "no-filters"}`}>
              {tab === "all" && this.renderFilters()}
              <div className="lib-results">
                {tab === "all" && result && <p className="hb-muted lib-count">{t("lib.results", { n: result.total })}</p>}
                {loading && !cards.length ? <p className="hb-muted">{t("loading")}</p> : null}
                {!loading && !cards.length && !error ? <p className="hb-muted lib-empty">{t(tab === "favorites" ? "lib.noFavorites" : tab === "history" ? "lib.noHistory" : "lib.noResults")}</p> : null}
                <div className="lib-grid">{cards.map(this.renderCard)}</div>
                {tab === "all" && result && result.pages > 1 && (
                  <div className="lib-pager">
                    <button type="button" className="hb-btn hb-btn-ghost" disabled={result.page <= 0} onClick={() => this.setPage(result.page - 1)}>{t("lib.prev")}</button>
                    <span>{result.page + 1} / {result.pages}</span>
                    <button type="button" className="hb-btn hb-btn-ghost" disabled={result.page + 1 >= result.pages} onClick={() => this.setPage(result.page + 1)}>{t("lib.next")}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Library;
